import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './useOrganization';
import { queryKeys } from '@/lib/query-keys';

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AlertType =
  | 'health_degraded'
  | 'health_improved'
  | 'obligation_overdue'
  | 'obligation_at_risk'
  | 'tasks_stuck'
  | 'tasks_overdue'
  | 'project_stalled'
  | 'client_declining';

export interface Alert {
  id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  title: string;
  description: string | null;
  client_id: string | null;
  client_name: string | null;
  project_id: string | null;
  project_name: string | null;
  triggered_at: string;
  acknowledged_at: string | null;
}

export interface AlertCounts {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  unacknowledged: number;
}

// Fetch active alerts
export function useAlerts() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: queryKeys.alerts.all(organizationId!),
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_active_alerts', {
        p_organization_id: organizationId,
      });

      if (error) throw error;
      return (data || []) as Alert[];
    },
    enabled: !!organizationId,
    refetchInterval: 60000, // Refetch every minute
  });
}

// Fetch alert counts
export function useAlertCounts() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: queryKeys.alerts.counts(organizationId!),
    queryFn: async () => {
      if (!organizationId) {
        return { total: 0, critical: 0, high: 0, medium: 0, low: 0, unacknowledged: 0 };
      }

      const { data, error } = await supabase.rpc('get_alert_counts', {
        p_organization_id: organizationId,
      });

      if (error) throw error;
      return (data || { total: 0, critical: 0, high: 0, medium: 0, low: 0, unacknowledged: 0 }) as AlertCounts;
    },
    enabled: !!organizationId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

// Acknowledge an alert
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('acknowledge_alert', {
        p_alert_id: alertId,
        p_user_id: user.id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all(organizationId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.counts(organizationId!) });
    },
  });
}

// Dismiss an alert
export function useDismissAlert() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('dismiss_alert', {
        p_alert_id: alertId,
        p_user_id: user.id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all(organizationId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.counts(organizationId!) });
    },
  });
}

// Resolve an alert
export function useResolveAlert() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase.rpc('resolve_alert', {
        p_alert_id: alertId,
        p_user_id: user.id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all(organizationId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.alerts.counts(organizationId!) });
    },
  });
}

// Real-time subscription for alerts
export function useAlertsSubscription() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: queryKeys.alerts.subscription(organizationId!),
    queryFn: async () => {
      if (!organizationId) return null;

      const channel = supabase
        .channel(`alerts-${organizationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'health_alerts',
            filter: `organization_id=eq.${organizationId}`,
          },
          () => {
            // Invalidate queries when alerts change
            queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all(organizationId!) });
            queryClient.invalidateQueries({ queryKey: queryKeys.alerts.counts(organizationId!) });
          }
        )
        .subscribe();

      return channel;
    },
    enabled: !!organizationId,
    staleTime: Infinity,
  });
}

// Helper to get severity color
export function getSeverityColor(severity: AlertSeverity): string {
  switch (severity) {
    case 'critical':
      return 'text-destructive bg-destructive/10';
    case 'high':
      return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
    case 'medium':
      return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30';
    case 'low':
      return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
    default:
      return 'text-muted-foreground bg-muted';
  }
}

// Helper to get alert type label
export function getAlertTypeLabel(type: AlertType): string {
  switch (type) {
    case 'health_degraded':
      return 'Health Degraded';
    case 'health_improved':
      return 'Health Improved';
    case 'obligation_overdue':
      return 'Obligation Overdue';
    case 'obligation_at_risk':
      return 'Obligation At Risk';
    case 'tasks_stuck':
      return 'Tasks Stuck';
    case 'tasks_overdue':
      return 'Tasks Overdue';
    case 'project_stalled':
      return 'Project Stalled';
    case 'client_declining':
      return 'Client Declining';
    default:
      return type;
  }
}
